import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { Badge } from '../../core/Badge/Badge.js';
import './StatusBadge.css';

export type StatusTone = 'healthy' | 'degraded' | 'warning' | 'down' | 'inactive' | 'pending';

export interface StatusBadgeProps extends Omit<BaseProps, 'color' | 'variant'> {
  /** Semantic status: drives color + default label. */
  status: StatusTone;
  /** Override the label shown next to the dot. Defaults to the status name (Title-cased). */
  children?: ReactNode;
  /** Show the leading dot (default true). */
  withDot?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const TONE_COLOR: Record<StatusTone, string> = {
  healthy: 'green',
  degraded: 'yellow',
  warning: 'orange',
  down: 'red',
  inactive: 'gray',
  pending: 'blue'
};

const TONE_LABEL: Record<StatusTone, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  warning: 'Warning',
  down: 'Down',
  inactive: 'Inactive',
  pending: 'Pending'
};

/**
 * Semantic status pill with a leading dot. Use for service health, channel
 * state, subscription state: anywhere a healthy/degraded/down kind of signal
 * shows up. Wraps the standard `Badge` so all theming/animation props pass
 * through.
 */
export const StatusBadge = forwardRef(function StatusBadge(
  { status, children, withDot = true, size = 'sm', className, ...rest }: StatusBadgeProps,
  ref: Ref<HTMLSpanElement>
): ReactNode {
  const label = children ?? TONE_LABEL[status];
  return (
    <Badge
      ref={ref}
      size={size}
      variant="light"
      color={TONE_COLOR[status]}
      className={['rr-status-badge', `rr-status-badge--${status}`, className].filter(Boolean).join(' ')}
      leftSection={withDot ? <span className="rr-status-badge__dot" aria-hidden /> : undefined}
      {...rest}
    >
      {label}
    </Badge>
  );
});
